import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid2,
  Skeleton,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { Point } from "pixi.js";
import { Sprite } from "@pixi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { BookingFilterButtons } from "../../BookingForm/BookingFilter/BookingFilterButtonGroup.component";
import { FloorFilters } from "../../BookingForm/BookingFilter/FloorFilters.component";
import { generateFloorButtonLabel } from "../../BookingForm/BookingFilter/functions/FloorFilter.functions";
import { GroupFilters } from "../../BookingForm/BookingFilter/GroupFilters.component";
import {
  ColleagueFilterState,
  GroupFilterSelected
} from "../../BookingForm/BookingFilter/types/GroupFilters.type";
import { BookingFilterMode } from "../../BookingForm/Domain/booking-filter-mode-enum.type";
import {
  applyBookingFilterOnFloorPlan,
  handleInitialPreferredCategoryFilter,
  initCategoryOptions,
  initFilterOptions
} from "../../BookingForm/functions/form.functions";
import { SlideoutPanelDialog } from "../../SlideoutPanel/SlideoutPanelDialog.component";
import { useBackgroundImage } from "../../FacilityManager/Hooks/useBackgroundImage";
import { DisplayViewport } from "../../FacilityManager/Components/DisplayViewport/DisplayViewport";
import { ZoneContainer } from "../../FacilityManager/Components/Zone/ZoneContainer";
import { PlaceContainer } from "../../FacilityManager/Components/Place/PlaceContainer";
import { EmptyBackgroundText } from "../../FacilityManager/Components/Canvas/Partials/EmptyBackgroundText.partial";
import { PlanViewMode } from "../../FacilityManager/Components/PixiViewport/PixiViewport";
import type { IViewport } from "../../FacilityManager/Domain/Types/FloorPlan/Viewport.type";
import {
  generateBookingPlaceVariant,
  generateZoneVariant
} from "../../FacilityManager/Functions/generateVariant";
import { useGeometry } from "../../FacilityManager/Hooks/useGeometry";
import { calculateWarnRatio } from "../../FacilityManager/Components/Zone/ZoneWarnGraphic";
import {
  isZoneClickable,
  checkUserNumberBooking,
  isPlaceClickable,
  generateInitial
} from "../../FacilityManager/Components/Views/SelectFloorPlanView/Functions/SelectFloorPlanView.functions";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import type { NotSerializedBookingInputs } from "../../../features/Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import type { ChangeConferenceRoomSelection } from "./change-conference-room.types";
import {
  buildBookingFiltersFromGroupFilterItems,
  buildSyntheticBookingInputsFromSchedule,
  CHANGE_ROOM_CURRENT_ZONE_FILL_ALPHA,
  CHANGE_ROOM_CURRENT_ZONE_FILL_COLOR,
  CHANGE_ROOM_SELECTED_ZONE_FILL_COLOR,
  resolveCurrentBookingInventoryId,
  computeChangeRoomSeatStatus,
  findZoneDisplayName,
  findZoneScheduleIdByInventoryId,
  mergeBookingFiltersIntoInputs,
  resolveFloorInventoryIdFromSchedule,
  type ChangeRoomBookingFilters
} from "./change-conference-room.functions";
import { useChangeConferenceRoomFloorData } from "./useChangeConferenceRoomFloorData.hook";
import { useRemoteFetchBookingFavoritesByFloor } from "../../../hooks/Remote/BookingFavorites/useRemoteFetchBookingFavoritesByFloor";
import { getFavoriteInventoryIdsSetForMode } from "../../BookingForm/functions/form-workplace-favorites.functions";

type P = {
  schedule: BookingScheduleInterface;
  currentSelection?: ChangeConferenceRoomSelection | null;
  onSelectionChange: (selection: ChangeConferenceRoomSelection | null) => void;
};

export function ChangeConferenceRoomFloorStep({
  schedule,
  currentSelection,
  onSelectionChange
}: Readonly<P>) {
  const { t } = useTranslation();
  const { userInformation, settings } = useSelector((state: RootState) => state.login);
  const { preferredPlaceCategories } = settings;
  const { convertPixiPoints } = useGeometry();

  const scheduleFloorInventoryId = resolveFloorInventoryIdFromSchedule(schedule) ?? 0;
  const [selectedFloorInventoryId, setSelectedFloorInventoryId] =
    useState(scheduleFloorInventoryId);
  const [activeTouchCount, setActiveTouchCount] = useState(0);
  const [viewportDimensions, setViewportDimensions] = useState<IViewport>({ width: 0, height: 0 });
  const [placeScale, setPlaceScale] = useState(0);
  const [warnScale, setWarnScale] = useState(1);
  const [isFloorFilterOpen, setIsFloorFilterOpen] = useState(false);
  const [isGroupFilterOpen, setIsGroupFilterOpen] = useState(false);
  const [detailGroupFilter, setDetailGroupFilter] = useState(false);
  const [filterItems, setFilterItems] = useState<GroupFilterSelected[]>([]);
  const [filterMode, setFilterMode] = useState<BookingFilterMode>(BookingFilterMode.INITIAL);
  const [filteredSelectables, setFilteredSelectables] = useState<number[] | undefined>();
  const [bookingFilters, setBookingFilters] = useState<ChangeRoomBookingFilters | undefined>();

  const {
    floorInventory,
    currentFloor,
    currentFloorIndex,
    floorPlan,
    isLoading,
    isError,
    refetch
  } = useChangeConferenceRoomFloorData(schedule, true, selectedFloorInventoryId);

  const baseBookingInputs = useMemo(
    () => buildSyntheticBookingInputsFromSchedule(schedule),
    [schedule]
  );

  const bookingInputs = useMemo(
    () => mergeBookingFiltersIntoInputs(baseBookingInputs, bookingFilters),
    [baseBookingInputs, bookingFilters]
  );

  const warningData = useMemo(
    () => ({
      requiredUserWarningData: currentFloor?.requiredUserWarningData ?? null,
      optionalUserWarningData: currentFloor?.optionalUserWarningData ?? null
    }),
    [currentFloor]
  );

  const seatStatus = useMemo(
    () => computeChangeRoomSeatStatus(currentFloor, filteredSelectables),
    [currentFloor, filteredSelectables]
  );

  const floorInventoryIdForFavorites = floorPlan?.floorInventoryId ?? selectedFloorInventoryId;

  const { data: floorFavoritesData } = useRemoteFetchBookingFavoritesByFloor(
    floorInventoryIdForFavorites > 0 ? floorInventoryIdForFavorites : null
  );

  const favoriteInventoryIds = useMemo(
    () =>
      getFavoriteInventoryIdsSetForMode(floorFavoritesData?.favorites, BookingType.CONFERENCEZONE),
    [floorFavoritesData?.favorites]
  );

  const usedCategory = useMemo(() => {
    if (!floorPlan || !currentFloor) {
      return { type: "zone" as const, options: [] };
    }
    return initCategoryOptions(BookingType.CONFERENCEZONE, floorPlan, currentFloor);
  }, [floorPlan, currentFloor]);

  const usedOptions = useMemo(
    () =>
      initFilterOptions(BookingType.CONFERENCEZONE, floorPlan) ?? {
        usedEquipment: [],
        usedEquipmentCategories: [],
        usedBookingProperties: []
      },
    [floorPlan]
  );

  const isFilterEntryEmpty =
    usedCategory.options.length === 0 &&
    usedOptions.usedBookingProperties.length === 0 &&
    usedOptions.usedEquipmentCategories.length === 0 &&
    usedOptions.usedEquipment.length === 0;

  const [selectedZoneScheduleId, setSelectedZoneScheduleId] = useState<number | undefined>(
    () => currentSelection?.newZoneScheduleId
  );

  useEffect(() => {
    if (currentSelection?.newZoneScheduleId != null) {
      setSelectedZoneScheduleId(currentSelection.newZoneScheduleId);
    }
  }, [currentSelection?.newZoneScheduleId]);

  const { background, isEmptyBgUsed, unsetBackgroundImage } = useBackgroundImage({
    backgroundImageUrl: floorPlan?.outlineUrl,
    viewport: floorPlan?.viewport
  });

  /** Remount viewport when the loaded floor plan changes so zoom/center match the new floor. */
  const viewportFloorKey = floorPlan?.floorInventoryId ?? selectedFloorInventoryId;

  useEffect(() => {
    if (!background || !floorPlan) {
      return;
    }
    setPlaceScale(background.width / floorPlan.viewport.width);
  }, [background, floorPlan]);

  useEffect(() => {
    if (!background) {
      return;
    }
    const { width: bgWidth, height: bgHeight } = background;
    const { width: vdWidth, height: vdHeight } = viewportDimensions;
    if (!vdWidth || !vdHeight || !bgWidth || !bgHeight) {
      return;
    }
    setWarnScale(calculateWarnRatio(vdWidth, vdHeight, bgWidth, bgHeight));
  }, [background, viewportDimensions]);

  useEffect(() => {
    if (floorPlan && currentFloor) {
      handleInitialPreferredCategoryFilter({
        preferredPlaceCategories,
        floorPlan,
        usedCategory,
        filterMode,
        isGroupFilterOpen,
        setFilteredSelectables,
        setFilterItems
      });
    }
  }, [floorPlan, currentFloorIndex, isGroupFilterOpen]);

  useEffect(() => {
    if (floorPlan) {
      applyBookingFilterOnFloorPlan(
        bookingInputs as unknown as NotSerializedBookingInputs,
        floorPlan,
        filterItems,
        filterMode,
        setFilteredSelectables
      );
    }
  }, [bookingInputs, floorPlan, filterItems, currentFloorIndex, filterMode]);

  const currentInventoryId = resolveCurrentBookingInventoryId(schedule);
  const usersBookedFor = bookingInputs.usersBookedFor;

  const emitSelection = useCallback(
    (zoneScheduleId: number | undefined) => {
      if (zoneScheduleId == null || !floorPlan) {
        onSelectionChange(null);
        return;
      }
      const zone = floorPlan.zones.find(z => z.id === zoneScheduleId);
      const inventoryId = zone?.inventory?.id ?? zone?.inventoryId;
      if (inventoryId == null || inventoryId === currentInventoryId) {
        onSelectionChange(null);
        return;
      }
      const newFloorInventoryId = floorPlan.floorInventoryId;
      if (newFloorInventoryId <= 0) {
        onSelectionChange(null);
        return;
      }
      onSelectionChange({
        newBookingInventoryId: inventoryId,
        newZoneScheduleId: zoneScheduleId,
        newFloorInventoryId,
        newRoomLabel: findZoneDisplayName(floorPlan, inventoryId, schedule.locationName)
      });
    },
    [currentInventoryId, floorPlan, onSelectionChange, schedule.locationName]
  );

  const applyZoneSelection = useCallback(
    (zoneScheduleId: number | undefined) => {
      setSelectedZoneScheduleId(zoneScheduleId);
      emitSelection(zoneScheduleId);
    },
    [emitSelection]
  );

  useEffect(() => {
    setSelectedFloorInventoryId(scheduleFloorInventoryId);
    setSelectedZoneScheduleId(undefined);
    onSelectionChange(null);
    setFilterItems([]);
    setFilteredSelectables(undefined);
    setBookingFilters(undefined);
    setFilterMode(BookingFilterMode.INITIAL);
  }, [schedule.id]);

  const handleZoneClick = (zoneScheduleId: number) => {
    applyZoneSelection(zoneScheduleId);
  };

  const handleSelectFloor = (id: number) => {
    if (currentFloor?.id === id) {
      setIsFloorFilterOpen(false);
      return;
    }
    setSelectedFloorInventoryId(id);
    applyZoneSelection(undefined);
    unsetBackgroundImage();
    setIsFloorFilterOpen(false);
    setFilterMode(BookingFilterMode.INITIAL);
  };

  const isFilterAble = Boolean(floorPlan && !isLoading && background);

  if (isLoading) {
    return (
      <>
        <Skeleton
          variant="rectangular"
          data-testid="change-room-floor-filter-button-1-loading"
          sx={{ width: 220, height: 16 }}
        />
        <Box display="flex" flexDirection="row" gap={1}>
          <Skeleton
            variant="rectangular"
            data-testid="change-room-floor-filter-button-1-loading"
            sx={{ width: 160, height: 40 }}
          />
          <Skeleton
            variant="rectangular"
            data-testid="change-room-floor-filter-button-2-loading"
            sx={{ width: 110, height: 40 }}
          />
        </Box>
        <Skeleton
          variant="rectangular"
          data-testid="change-room-floor-loading"
          sx={{
            width: "100%",
            height: { xs: "50vh", md: "55vh" },
            minHeight: { xs: 240, sm: 320 }
          }}
        />
      </>
    );
  }

  if (isError || !floorPlan || !currentFloor) {
    return (
      <Stack spacing={1} alignItems="center" data-testid="change-room-floor-error">
        <Typography color="error">{t("_changeRoom_floorLoadError")}</Typography>
        <Button variant="outlined" onClick={() => refetch()}>
          {t("Retry")}
        </Button>
      </Stack>
    );
  }

  const highlightCurrentZoneId = findZoneScheduleIdByInventoryId(floorPlan, currentInventoryId);

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {t("_changeRoom_selectRoomHint")}
      </Typography>

      <Grid2 sx={{ width: "100%" }} data-testid="change-room-floor-filters">
        <BookingFilterButtons
          bookingMode={BookingType.CONFERENCEZONE}
          floorAvailability={currentFloor}
          floorButtonLabel={generateFloorButtonLabel(floorInventory, currentFloorIndex)}
          colleagueFilterState={ColleagueFilterState.DEACTIVATED}
          setIsFloorFilterOpen={() => setIsFloorFilterOpen(true)}
          setIsGroupFilterOpen={() => setIsGroupFilterOpen(true)}
          setColleagueFilterState={() => undefined}
          setIsDateOpen={() => undefined}
          setIsTeamBookingOpen={() => undefined}
          filterItems={filterItems}
          selectedColleagueFilter={[]}
          setSelectedColleagueFilter={() => undefined}
          isFilterEntryEmpty={isFilterEntryEmpty}
          isFilterAble={isFilterAble}
        />

        <SlideoutPanelDialog
          isOpen={isFloorFilterOpen}
          dialogTitle={t("Floor")}
          width="20vw"
          height="0px"
          showCloseButton
          onClose={() => setIsFloorFilterOpen(false)}
        >
          <FloorFilters
            floorInventory={floorInventory}
            currentFloor={currentFloor}
            onSelectFloor={id => handleSelectFloor(id)}
          />
        </SlideoutPanelDialog>

        <SlideoutPanelDialog
          isOpen={isGroupFilterOpen}
          dialogTitle={t("Filter")}
          width={detailGroupFilter ? "35vw" : "25vw"}
          height="0px"
          showCloseButton
          showDetailToggle
          detailGroupFilterToggle={
            <FormControlLabel
              sx={{ width: "100%" }}
              control={
                <Switch
                  data-testid="change-room-show-detail-toggle"
                  checked={detailGroupFilter}
                  onChange={() => setDetailGroupFilter(prev => !prev)}
                />
              }
              label={detailGroupFilter ? t("Show details") : t("Hide details")}
              slotProps={{ typography: { sx: { width: "fit-content" } } }}
            />
          }
          onClose={() => {
            setIsGroupFilterOpen(false);
            setDetailGroupFilter(false);
          }}
          primaryButtonTitle={t("Apply")}
          secondaryButtonTitle={t("Reset")}
          primaryButtonAction={() => {
            if (
              filterMode === BookingFilterMode.INITIAL ||
              filterMode === BookingFilterMode.RESET
            ) {
              setFilterMode(BookingFilterMode.FILTERING);
            }
            setBookingFilters(buildBookingFiltersFromGroupFilterItems(filterItems));
          }}
          secondaryButtonAction={() => {
            setFilterItems([]);
            setFilteredSelectables(undefined);
            setBookingFilters(undefined);
            setFilterMode(BookingFilterMode.RESET);
          }}
        >
          <GroupFilters
            floorPlan={floorPlan}
            showDetailGroupFilter={detailGroupFilter}
            filterItems={filterItems}
            setFilterItems={setFilterItems}
            usedCategory={usedCategory}
            usedOptions={usedOptions}
            filterMode={filterMode}
            isCapacityReportUsed={false}
          />
        </SlideoutPanelDialog>
      </Grid2>

      <Box
        data-testid="change-room-floor-plan"
        sx={{
          width: "100%",
          height: { xs: "50vh", md: "55vh" },
          minHeight: { xs: 240, sm: 320 },
          overflow: "hidden",
          borderRadius: 1,
          border: theme => `1px solid ${theme.palette.divider}`,
          position: "relative"
        }}
      >
        {background && !!placeScale && (
          <DisplayViewport
            key={viewportFloorKey}
            planViewMode={PlanViewMode.BOOKING}
            floorPlanName="change-room"
            initialZoomEnd={new Point(background.width, background.height)}
            currentFloorPlan={floorPlan}
            placeScale={placeScale}
            viewportDimensions={viewportDimensions}
            setViewportDimensions={setViewportDimensions}
            setActiveTouchCount={setActiveTouchCount}
          >
            {!isEmptyBgUsed && <Sprite texture={background} />}
            {isEmptyBgUsed && (
              <EmptyBackgroundText
                rectWidth={floorPlan.viewport.width}
                rectHeight={floorPlan.viewport.height}
                posY={5}
              />
            )}
            {floorPlan.zones.map(zone => {
              const isNewlySelectedZone = zone.id === selectedZoneScheduleId;
              const isCurrentBookingZone =
                highlightCurrentZoneId != null &&
                zone.id === highlightCurrentZoneId &&
                !isNewlySelectedZone;

              return (
                <ZoneContainer
                  id={zone.id}
                  key={zone.id}
                  inventoryId={zone.inventory?.id}
                  walls={convertPixiPoints(zone.coordinates)}
                  disabled={zone.disabled}
                  zoneType={zone.inventory?.zoneTypeId as number}
                  onClick={handleZoneClick}
                  isClickable={isZoneClickable({
                    bookingType: BookingType.CONFERENCEZONE,
                    zoneTypeId: zone.inventory?.zoneTypeId,
                    zoneInventoryId: zone.inventory?.id as number,
                    seatStatus
                  })}
                  isSelected={isNewlySelectedZone}
                  selectedFillColor={CHANGE_ROOM_SELECTED_ZONE_FILL_COLOR}
                  isReferenceHighlighted={isCurrentBookingZone}
                  referenceFillColor={CHANGE_ROOM_CURRENT_ZONE_FILL_COLOR}
                  referenceFillAlpha={CHANGE_ROOM_CURRENT_ZONE_FILL_ALPHA}
                  showWarning={checkUserNumberBooking(
                    BookingType.CONFERENCEZONE,
                    zone.inventory?.minUsers,
                    zone.inventory?.maxUsers,
                    bookingInputs.zoneAccess,
                    warningData
                  )}
                  isFavorite={
                    favoriteInventoryIds != null &&
                    zone.inventory?.id != null &&
                    favoriteInventoryIds.has(zone.inventory.id)
                  }
                  interactiveConferenceWarning={false}
                  setIsHoverWarning={() => undefined}
                  activeTouchCount={activeTouchCount}
                  warnScale={warnScale}
                  variant={generateZoneVariant(zone, seatStatus)}
                />
              );
            })}

            {floorPlan.places.map(workplace => {
              const inventoryId = workplace.inventoryId || workplace.inventory?.id;
              const placeTypeId = workplace.placeTypeId || workplace.inventory?.placeTypeId;
              const isPlaceSelectable = isPlaceClickable(
                BookingType.CONFERENCEZONE,
                placeTypeId,
                inventoryId,
                seatStatus
              );
              const isFavoritePlace =
                favoriteInventoryIds != null &&
                inventoryId != null &&
                favoriteInventoryIds.has(inventoryId);
              return (
                <PlaceContainer
                  key={workplace.id}
                  boundingBox={{
                    height: workplace.inventory?.boundingBox?.height ?? 80,
                    width: workplace.inventory?.boundingBox?.width ?? 160
                  }}
                  inventoryId={inventoryId}
                  id={workplace.id}
                  position={workplace.position}
                  rotation={workplace.rotate}
                  isHighlighted={isFavoritePlace}
                  isFavorite={isFavoritePlace}
                  isSelected={false}
                  onClick={() => undefined}
                  isSelectable={isPlaceSelectable}
                  eventMode={isPlaceSelectable ? "static" : "none"}
                  variant={generateBookingPlaceVariant(
                    { workplace, zone: undefined },
                    seatStatus,
                    BookingType.CONFERENCEZONE
                  )}
                  icon={generateInitial(
                    "booking",
                    usersBookedFor,
                    userInformation,
                    [],
                    inventoryId
                  )}
                  placeTypeId={placeTypeId}
                  placeScale={placeScale}
                  activeTouchCount={activeTouchCount}
                />
              );
            })}
          </DisplayViewport>
        )}
      </Box>
    </Stack>
  );
}
